import React, { useEffect, useRef, useMemo } from 'react';
import { MapContainer, TileLayer, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet.markercluster';

// Esri World Street Map — keyless, no signup.
//
// History of this line, so nobody walks back into either dead end:
//   CARTO  — now requires a key, stamps "API KEY REQUIRED" across every tile.
//   OSM    — tile.openstreetmap.org started returning 403 "Access blocked / App
//            is not following the tile usage policy". Their volunteer servers
//            identify clients by User-Agent, which a browser app cannot set, so
//            shared corporate egress gets blocked with no way to appeal from the
//            client side. Their own policy points heavy users at exactly this
//            kind of alternative.
//
// CRITICAL — axis order is {z}/{y}/{x}, ROW BEFORE COLUMN. This is the reverse
// of OSM/CARTO's {z}/{x}/{y}. Swapping them does NOT fail loudly: the server
// returns HTTP 200 with a perfectly valid tile of the wrong place (z6/31/21
// renders Guyana instead of the UK), so the map looks like it works while
// showing another continent. Verified correct as written.
//
// No {s} subdomain — Esri serves from the single host.
const TILE_URL = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Street_Map/MapServer/tile/{z}/{y}/{x}';
const TILE_ATTR = 'Tiles &copy; <a href="https://www.esri.com">Esri</a> — Esri, DeLorme, NAVTEQ, USGS, NRCAN';

function createMarkerIcon(device, isSelected) {
  const letter = device.type === 'Teltonika' ? 'T' : 'R';
  return L.divIcon({
    className: '',
    html: `<div class="dm-marker ${device.status}${isSelected ? ' selected' : ''}">${letter}</div>`,
    iconSize: [28, 28],
    iconAnchor: [14, 14],
  });
}

// FlyTo on selection
function MapController({ selectedDevice }) {
  const map = useMap();
  useEffect(() => {
    if (selectedDevice) {
      map.flyTo([selectedDevice.lat, selectedDevice.lng], 14, { duration: 0.8 });
    }
  }, [selectedDevice, map]);
  return null;
}

// Hands the Leaflet map instance up to the page so the control cluster can
// drive the native zoom control rather than reimplementing zoom behaviour.
function MapRefBridge({ mapRef }) {
  const map = useMap();
  useEffect(() => {
    if (mapRef) mapRef.current = map;
    return () => { if (mapRef && mapRef.current === map) mapRef.current = null; };
  }, [map, mapRef]);
  return null;
}

// Fit bounds control. Fires on the explicit "Fit all" button, and also whenever the set of
// plotted devices changes — narrowing to a batch or location is useless if the viewport stays
// on wherever the map happened to be.
function FitBoundsControl({ devices, fitTrigger }) {
  const map = useMap();
  const key = devices.map(d => d.id).join(',');
  useEffect(() => {
    if (devices.length === 0) return;
    const bounds = L.latLngBounds(devices.map(d => [d.lat, d.lng]));
    map.fitBounds(bounds, { padding: [40, 40], maxZoom: 15 });
    // `key` stands in for the device set; `devices` itself is a new array each render.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fitTrigger, key, map]);
  return null;
}

// Manages the MarkerClusterGroup layer imperatively
function ClusterLayer({ devices, selectedDeviceId, onSelectDevice, clusterEnabled }) {
  const map = useMap();
  const clusterGroupRef = useRef(null);
  const markersLayerRef = useRef(null);

  useEffect(() => {
    // Create both layers once
    if (!clusterGroupRef.current) {
      clusterGroupRef.current = L.markerClusterGroup({
        maxClusterRadius: 50,
        spiderfyOnMaxZoom: true,
        showCoverageOnHover: false,
        iconCreateFunction: (cluster) => {
          const count = cluster.getChildCount();
          return L.divIcon({
            className: '',
            html: `<div class="dm-cluster">${count}</div>`,
            iconSize: [36, 36],
            iconAnchor: [18, 18],
          });
        },
      });
    }
    if (!markersLayerRef.current) {
      markersLayerRef.current = L.layerGroup();
    }
  }, []);

  useEffect(() => {
    const clusterGroup = clusterGroupRef.current;
    const markersLayer = markersLayerRef.current;
    if (!clusterGroup || !markersLayer) return;

    // Clear both
    clusterGroup.clearLayers();
    markersLayer.clearLayers();

    // Remove both from map
    if (map.hasLayer(clusterGroup)) map.removeLayer(clusterGroup);
    if (map.hasLayer(markersLayer)) map.removeLayer(markersLayer);

    // Build markers
    devices.forEach(d => {
      const icon = createMarkerIcon(d, d.id === selectedDeviceId);
      const marker = L.marker([d.lat, d.lng], {
        icon,
        // Markers are the primary way into a device on this page, so they are
        // focusable and operable by keyboard, with a name a screen reader can use.
        keyboard: true,
        title: `${d.name} — ${d.status}`,
        alt: `${d.name}, ${d.status}`,
      });
      marker.on('click', () => onSelectDevice(d.id));

      if (clusterEnabled) {
        clusterGroup.addLayer(marker);
      } else {
        markersLayer.addLayer(marker);
      }
    });

    // Add the active layer to the map
    if (clusterEnabled) {
      map.addLayer(clusterGroup);
    } else {
      map.addLayer(markersLayer);
    }

    return () => {
      if (map.hasLayer(clusterGroup)) map.removeLayer(clusterGroup);
      if (map.hasLayer(markersLayer)) map.removeLayer(markersLayer);
    };
  }, [devices, selectedDeviceId, onSelectDevice, clusterEnabled, map]);

  return null;
}

const DeviceMap = React.memo(function DeviceMap({
  // `darkMode` is intentionally not destructured: the basemap is the same in
  // both themes, and the marker/panel theming is handled in CSS. The page
  // still passes it, so re-adding a dark basemap later needs no plumbing.
  devices, selectedDeviceId, onSelectDevice, fitTrigger, clusterEnabled, mapRef
}) {
  const selectedDevice = useMemo(
    () => devices.find(d => d.id === selectedDeviceId) || null,
    [devices, selectedDeviceId]
  );

  return (
    <div className="dm-map-wrap">
      <MapContainer
        center={[52.5, -1.5]}
        zoom={6}
        style={{ width: '100%', height: '100%' }}
        zoomControl={true}
      >
        {/* One basemap for both themes — there is no keyless dark road map,
            and the panels and markers carry the theme instead. */}
        <TileLayer url={TILE_URL} attribution={TILE_ATTR} maxZoom={19} />
        <ClusterLayer
          devices={devices}
          selectedDeviceId={selectedDeviceId}
          onSelectDevice={onSelectDevice}
          clusterEnabled={clusterEnabled}
        />
        <MapController selectedDevice={selectedDevice} />
        <FitBoundsControl devices={devices} fitTrigger={fitTrigger} />
        <MapRefBridge mapRef={mapRef} />
      </MapContainer>
    </div>
  );
});

export default DeviceMap;
